/**
 * Cross-fading photo gallery for variety pages, with no dependencies so a failed module graph cannot hide
 * photo one. Auto-advance runs only with data-autorotate and without reduced motion; hover, focus, a hidden
 * tab or scrolling away pause it; any manual move stops it for good.
 */

const MIN_HOLD_MS = 2000;
const DEFAULT_HOLD_MS = 5000;
const SWIPE_THRESHOLD_PX = 40;

/** Stands in only if the snippet printed no data-status-words. */
const STATUS_WORDS = 'Photo [index] of [count]';

/** A manual stop holds for this page only, so each variety page starts rotating afresh. */
function readManualFlag() {
  return false;
}

function writeManualFlag() {
}

class VarietyGallery extends HTMLElement {
  #controller = null;
  #timer = null;
  #observer = null;
  #taken = false;
  #hovered = false;
  #focused = false;
  #offscreen = false;
  #touchX = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const slides = this.#slides();
    if (slides.length < 2) return;

    if (readManualFlag()) this.#taken = true;

    this.#bind('[data-variety-prev]', () => this.#step(-1), signal);
    this.#bind('[data-variety-next]', () => this.#step(1), signal);

    // One listener on the gallery; the row is looked up at the moment of the click.
    this.addEventListener('click', this.#onThumbClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerenter', () => this.#setPaused('hover', true), { signal });
    this.addEventListener('pointerleave', () => this.#setPaused('hover', false), { signal });
    this.addEventListener('focusin', () => this.#setPaused('focus', true), { signal });
    this.addEventListener('focusout', () => this.#setPaused('focus', false), { signal });
    this.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { signal, passive: true });
    document.addEventListener('visibilitychange', this.#onVisibility, { signal });

    if (typeof IntersectionObserver === 'function') {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.#offscreen = !entry.isIntersecting;
          this.#restart();
        },
        { threshold: 0.15 }
      );
      this.#observer.observe(this);
    }

    this.#restart();
  }

  disconnectedCallback() {
    this.#stopTimer();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#controller?.abort();
    this.#controller = null;
  }

  /** Re-read on every use, so a section morph cannot leave them stale. */
  #slides() {
    return Array.from(this.querySelectorAll('[data-variety-slide]'));
  }

  #thumbs() {
    return Array.from(this.querySelectorAll('[data-variety-thumb]'));
  }

  #dots() {
    return Array.from(this.querySelectorAll('[data-variety-dot]'));
  }

  #current() {
    const index = this.#slides().findIndex((slide) => slide.classList.contains('is-active'));
    return index < 0 ? 0 : index;
  }

  #bind(selector, handler, signal) {
    const button = this.querySelector(selector);
    if (!button) return;
    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        handler();
      },
      { signal }
    );
  }

  get #holdMs() {
    const parsed = parseInt(this.dataset.holdMs || '', 10);
    if (Number.isNaN(parsed)) return DEFAULT_HOLD_MS;
    return Math.max(MIN_HOLD_MS, parsed);
  }

  get #canRotate() {
    if (this.#taken) return false;
    if (this.dataset.autorotate !== 'true') return false;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
    return this.#slides().length > 1;
  }

  #setPaused(reason, value) {
    if (reason === 'hover') this.#hovered = value;
    if (reason === 'focus') this.#focused = value;
    this.#restart();
  }

  #onVisibility = () => this.#restart();

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#step(1);
    }
  };

  #onThumbClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-variety-thumb]');
    if (!thumb) return;
    const index = this.#thumbs().indexOf(thumb);
    if (index < 0) return;
    event.preventDefault();
    this.#goTo(index);
  };

  #onTouchStart = (event) => {
    this.#touchX = event.changedTouches?.[0]?.clientX ?? null;
  };

  #onTouchEnd = (event) => {
    if (this.#touchX == null) return;
    const endX = event.changedTouches?.[0]?.clientX ?? null;
    const startX = this.#touchX;
    this.#touchX = null;
    if (endX == null) return;
    const distance = endX - startX;
    if (Math.abs(distance) < SWIPE_THRESHOLD_PX) return;
    this.#step(distance < 0 ? 1 : -1);
  };

  /** An arrow, an arrow key or a swipe: one photo along, by the visitor's hand. */
  #step(direction) {
    this.#goTo(this.#current() + direction);
  }

  /** A manual move hands control to the visitor for good, and is announced. */
  #goTo(index) {
    this.#taken = true;
    writeManualFlag();
    this.#stopTimer();
    this.#show(index, true);
  }

  /** The one place a photo changes: slides, thumbnails and dots are set together. */
  #show(index, announce) {
    const slides = this.#slides();
    if (slides.length < 2) return;

    const next = ((index % slides.length) + slides.length) % slides.length;

    slides.forEach((slide, position) => {
      const active = position === next;
      slide.classList.toggle('is-active', active);
      if (active) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      }
    });

    this.#thumbs().forEach((thumb, position) => {
      if (position === next) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    this.#dots().forEach((dot, position) => {
      dot.classList.toggle('is-current', position === next);
    });

    if (announce) {
      const status = this.querySelector('[data-variety-status]');
      if (status) status.textContent = this.#statusWords(next + 1, slides.length);
    }
  }

  #statusWords(index, count) {
    const words = this.dataset.statusWords || STATUS_WORDS;
    return words.replace('[index]', String(index)).replace('[count]', String(count));
  }

  #restart() {
    this.#stopTimer();
    if (!this.#canRotate) return;
    if (this.#hovered || this.#focused || this.#offscreen || document.hidden) return;
    this.#timer = window.setInterval(() => {
      if (document.hidden) return;
      this.#show(this.#current() + 1, false);
    }, this.#holdMs);
  }

  #stopTimer() {
    if (this.#timer != null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('variety-gallery')) {
  customElements.define('variety-gallery', VarietyGallery);
}
